import { useState, useMemo } from 'react';
import { Box, IconButton, Menu, MenuItem, Typography, Pagination, useMediaQuery, useTheme } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ConfirmDialog } from '@components/dialogs/ConfirmDialog';
import { useSearchEmployeesQuery, useDeleteEmployeeMutation } from '@features/redux/apis/employees';
import { useSelector } from 'react-redux';
import type { RootState } from '@features/redux/store';
import type { EmployeeSearchParams, EmployeeSearchResult } from '@/types/newEmployees';
import { colors, DataTable, TableCard, TableLink, StatusBadge } from '@components/design-system';
import { typography as typographyTokens, radii } from '@components/design-system/tokens';
import { ExportEmployees } from '@components/buttons/ExportEmployees';
import { MobileEmployeeCard, MobileEmployeeCardSkeleton } from './MobileEmployeeCard';

interface EmployeesTableProps {
    orgFilters: {
        companyIds: string;
        resellerIds: string;
        locationIds: string;
    };
    /** Optional search text passed from FiltersDrawer */
    searchText?: string;
    onDelete?: (e: EmployeeSearchResult) => void;
    onSelect?: (e: EmployeeSearchResult) => void;
}

const PAGE_SIZE = 12;

export const EmployeesTable = ({ orgFilters, searchText, onDelete, onSelect }: EmployeesTableProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [page, setPage] = useState<number>(1);

    const IdToken = useSelector((state: RootState) => state.auth.IdToken);

    const [deleteEmployee, { isLoading: isDeleting }] = useDeleteEmployeeMutation();

    const [pendingDelete, setPendingDelete] = useState<EmployeeSearchResult | null>(null);

    const searchParams: EmployeeSearchParams = useMemo(() => {
        return {
            pageNum: page - 1,
            pageSize: PAGE_SIZE,
            companyIds: orgFilters.companyIds || undefined,
            resellerIds: orgFilters.resellerIds || undefined,
            locationIds: orgFilters.locationIds || undefined,
            sortDirection: 'ASC',
            searchText: searchText?.trim() || undefined,
        };
    }, [orgFilters, page, searchText]);

    const { data, isLoading } = useSearchEmployeesQuery(searchParams);

    const employees = useMemo(() => data?.employees || [], [data]);

    const exportParams = useMemo(
        () => ({
            companyIds: orgFilters.companyIds || undefined,
            resellerIds: orgFilters.resellerIds || undefined,
            locationIds: orgFilters.locationIds || undefined,
            searchText: searchText?.trim() || undefined,
            sortDirection: 'ASC',
        }),
        [orgFilters, searchText]
    );

    const columns = useMemo(
        () => [
            {
                field: 'Name',
                headerName: 'Name',
                flex: 1,
                minWidth: 160,
                renderCell: ({ value }: { value: string }) => (
                    <Typography sx={{ fontWeight: 500, color: colors.neutral[900], fontSize: '0.8125rem' }}>
                        {value}
                    </Typography>
                ),
            },
            {
                field: 'Email',
                headerName: 'Email',
                flex: 1.2,
                minWidth: 200,
                renderCell: ({ value }: { value: string }) => (
                    <Typography sx={{ color: colors.neutral[600], fontSize: '0.8125rem' }}>{value}</Typography>
                ),
            },
            {
                field: 'ParentCompanyName',
                headerName: 'Company',
                flex: 1,
                minWidth: 150,
                renderCell: ({ row }: { row: EmployeeSearchResult }) =>
                    row.ParentCompanyID ? (
                        <TableLink to={`/manage/organizations?type=company&id=${row.ParentCompanyID}`}>
                            {row.ParentCompanyName}
                        </TableLink>
                    ) : (
                        <Typography sx={{ color: colors.neutral[400], fontSize: '0.8125rem' }}>-</Typography>
                    ),
            },
            {
                field: 'ResellerName',
                headerName: 'Reseller',
                flex: 1,
                minWidth: 150,
                renderCell: ({ row }: { row: EmployeeSearchResult }) =>
                    row.ResellerID ? (
                        <TableLink to={`/manage/organizations?type=reseller&id=${row.ResellerID}`}>
                            {row.ResellerName}
                        </TableLink>
                    ) : (
                        <Typography sx={{ color: colors.neutral[400], fontSize: '0.8125rem' }}>-</Typography>
                    ),
            },
            {
                field: 'LocationName',
                headerName: 'Location',
                flex: 1,
                minWidth: 150,
                renderCell: ({ row }: { row: EmployeeSearchResult }) =>
                    row.LocationID ? (
                        <TableLink to={`/manage/organizations?type=location&id=${row.LocationID}`}>
                            {row.LocationName}
                        </TableLink>
                    ) : (
                        <Typography sx={{ color: colors.neutral[400], fontSize: '0.8125rem' }}>-</Typography>
                    ),
            },
            {
                field: 'Status',
                headerName: 'Status',
                width: 120,
                renderCell: ({ row }: { row: EmployeeSearchResult }) => (
                    <StatusBadge
                        label={row.Status || 'Unknown'}
                        status={row.Status === 'Active' ? 'success' : 'neutral'}
                        size='small'
                    />
                ),
            },
            {
                field: 'actions',
                headerName: '',
                width: 60,
                sortable: false,
                renderCell: ({ row }: { row: EmployeeSearchResult }) => {
                    const RowActions = ({ targetRow }: { targetRow: EmployeeSearchResult }) => {
                        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

                        const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
                            event.stopPropagation();
                            setAnchorEl(event.currentTarget);
                        };

                        const handleClose = () => setAnchorEl(null);

                        const handleDelete = () => {
                            setPendingDelete(targetRow);
                            handleClose();
                        };

                        return (
                            <>
                                <IconButton size='small' onClick={handleOpen}>
                                    <MoreVertIcon fontSize='small' sx={{ color: colors.neutral[500] }} />
                                </IconButton>

                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleClose}
                                    onClick={(e) => e.stopPropagation()}
                                    PaperProps={{ sx: { minWidth: 140 } }}>
                                    <MenuItem onClick={handleDelete} dense sx={{ color: colors.error.main }}>
                                        <DeleteOutlineIcon sx={{ mr: 1 }} fontSize='small' />
                                        Delete
                                    </MenuItem>
                                </Menu>
                            </>
                        );
                    };

                    return <RowActions targetRow={row} />;
                },
            },
        ],
        []
    );

    const totalRecords = data?.totalRecords || 0;
    const startItem = (page - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(page * PAGE_SIZE, totalRecords);
    const infoText =
        totalRecords > 0 ? `Showing ${startItem}-${endItem} of ${totalRecords} employees` : 'No employees found';

    const totalPages = Math.ceil(totalRecords / PAGE_SIZE);

    // Mobile card view
    if (isMobile) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    backgroundColor: colors.neutral[100],
                    overflow: 'hidden',
                }}>
                {/* Header */}
                <Box
                    sx={{
                        px: 2,
                        pt: 2,
                        pb: 1,
                        backgroundColor: colors.neutral[0],
                        borderBottom: `1px solid ${colors.neutral[200]}`,
                    }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.sm,
                                color: colors.neutral[600],
                            }}>
                            {infoText}
                        </Typography>
                        <ExportEmployees params={exportParams} />
                    </Box>
                </Box>

                {/* Cards List */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        px: 2,
                        py: 2,
                    }}>
                    {isLoading ? (
                        Array.from({ length: 4 }).map((_, index) => <MobileEmployeeCardSkeleton key={index} />)
                    ) : employees.length > 0 ? (
                        employees.map((employee) => (
                            <MobileEmployeeCard
                                key={employee.EmployeeID}
                                employee={employee}
                                onClick={onSelect ? () => onSelect(employee) : undefined}
                                onDelete={() => setPendingDelete(employee)}
                            />
                        ))
                    ) : (
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 8,
                                color: colors.neutral[500],
                            }}>
                            <Typography>No employees found</Typography>
                        </Box>
                    )}
                </Box>

                {/* Pagination */}
                {totalPages > 1 && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 1.5,
                            backgroundColor: colors.neutral[0],
                            borderTop: `1px solid ${colors.neutral[200]}`,
                        }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, newPage) => setPage(newPage)}
                            color='primary'
                            size='small'
                            siblingCount={0}
                            boundaryCount={1}
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    borderRadius: `${radii.md}px`,
                                    minWidth: 32,
                                },
                            }}
                        />
                    </Box>
                )}

                {pendingDelete && (
                    <ConfirmDialog
                        confirmText={`Are you sure you want to delete employee ${pendingDelete.Name}?`}
                        onCancel={() => {
                            if (!isDeleting) setPendingDelete(null);
                        }}
                        onConfirm={async () => {
                            if (!pendingDelete) return;

                            try {
                                await deleteEmployee({ id: pendingDelete.EmployeeID!, token: IdToken! }).unwrap();
                            } finally {
                                setPendingDelete(null);
                            }
                        }}
                        isLoading={isDeleting}
                    />
                )}
            </Box>
        );
    }

    // Desktop table view
    return (
        <Box
            display='flex'
            flexDirection='column'
            flex={1}
            sx={{
                minWidth: 0,
                minHeight: 0,
                height: '100%',
                backgroundColor: colors.neutral[100],
                p: 2,
            }}>
            <TableCard
                title='Employees'
                actions={<ExportEmployees params={exportParams} />}
                sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <DataTable
                    rows={employees}
                    columns={columns as any}
                    getRowId={(row) => `employee-${row.EmployeeID}`}
                    totalRows={totalRecords}
                    page={page}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPage}
                    loading={isLoading}
                    height='100%'
                    infoText={infoText}
                    onRowClick={({ row }) => onSelect?.(row)}
                />
            </TableCard>

            {pendingDelete && (
                <ConfirmDialog
                    confirmText={`Are you sure you want to delete employee ${pendingDelete.Name}?`}
                    onCancel={() => {
                        if (!isDeleting) setPendingDelete(null);
                    }}
                    onConfirm={async () => {
                        if (!pendingDelete) return;

                        try {
                            await deleteEmployee({ id: pendingDelete.EmployeeID!, token: IdToken! }).unwrap();
                        } finally {
                            setPendingDelete(null);
                        }
                    }}
                    isLoading={isDeleting}
                />
            )}
        </Box>
    );
};
